// components/dashboard/dashboard.js - FIXED SELECTOR VERSION
import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useDispatch, useSelector } from 'react-redux';
import { 
  saveAllTableStatesForUser,
  setAllTableStates,
} from '../../redux/panelPositionsSlice';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';
import './dashboard.css';

import { RefDataTabs } from '../tabs/reference_data_tabs';
import BuyOrderBookTable3 from '../tables/orderbook_buy3';
import SellOrderBookTable3 from '../tables/orderbook_sell3';
import { OrderForm } from '../forms/order_form/order_form';
import { UserDataTabs } from '../tabs/user_data_tabs';
import { MiscTabs } from '../tabs/misc_tabs';

export const getDefaultLayout = () => ({
  lg: [
    { i: 'reference-tabs', x: 0, y: 0, w: 40, h: 10, minW: 15, maxW: 40, minH: 8, maxH: 40, moved: false, static: false },
    { i: 'buy-order-book', x: 0, y: 10, w: 8, h: 13.3, minW: 4, maxW: 15, minH: 8, maxH: 30, moved: false, static: false },
    { i: 'sell-order-book', x: 8, y: 10, w: 8, h: 13.3, minW: 4, maxW: 15, minH: 8, maxH: 30, moved: false, static: false },
    { i: 'order-form', x: 16, y: 10, w: 13.3, h: 13.3, minW: 10, maxW: 13.6, minH: 8, maxH: 14.6, moved: false, static: false },
    { i: 'users-tabs', x: 0, y: 23.3, w: 20, h: 10, minW: 15, maxW: 40, minH: 8, maxH: 40, moved: false, static: false },
    { i: 'misc-tables', x: 20, y: 23.3, w: 16, h: 10, minW: 10, maxW: 40, minH: 8, maxH: 40, moved: false, static: false }
  ]
});

const ResponsiveGridLayout = WidthProvider(Responsive);

// ========== CONSTANT EMPTY OBJECT (prevents re-renders) ==========
const EMPTY_TABLE_STATES = {};

const Dashboard = forwardRef(({ userId }, ref) => {
  const dispatch = useDispatch();
  
  // ========== FIXED: Memoized selector to prevent unnecessary re-renders ==========
  const tableStatesSelector = useMemo(
    () => (state) => {
      const userStates = state.panelPositions?.tableStates?.[userId];
      // Return the constant empty object if no states exist
      return userStates || EMPTY_TABLE_STATES;
    },
    [userId]
  );
  
  const tableStates = useSelector(tableStatesSelector);

  const GlobalStyles = React.createElement('style', {
    dangerouslySetInnerHTML: {
      __html: `
        .react-grid-item {
          transition: none !important;
        }
        .react-grid-item.cssTransforms {
          transition-property: none !important;
        }
        .react-grid-item.react-draggable-dragging {
          transition: none !important;
          z-index: 1000;
        }
        .dashboard-panel:focus,
        .dashboard-panel:active {
          outline: none !important;
          box-shadow: none !important;
        }
        .dashboard-panel *:focus {
          outline: none !important;
        }
      `
    }
  });

  const [layouts, setLayouts] = useState(getDefaultLayout());
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  const [isMounted, setIsMounted] = useState(false);

  const validateLayout = useCallback((layout) => {
    return layout.map(item => {
      let { w, minW, maxW } = item;
      
      minW = Math.max(1, minW || 2);
      maxW = Math.max(minW, maxW || 12);
      w = Math.max(minW, Math.min(maxW, w || 4));
      
      return {
        ...item,
        w,
        minW,
        maxW,
        minH: Math.max(2, item.minH || 2),
        maxH: Math.max(item.minH || 2, item.maxH || 40),
        h: Math.max(item.minH || 2, Math.min(item.maxH || 40, item.h || 4))
      };
    });
  }, []);

  const getCurrentLayout = useCallback(() => {
    const preferredLayout = layouts.lg || layouts[currentBreakpoint];
    const currentLayout = preferredLayout || getDefaultLayout().lg;
    
    if (!currentLayout || !Array.isArray(currentLayout)) {
      return validateLayout(getDefaultLayout().lg);
    }
    return validateLayout(currentLayout);
  }, [layouts, currentBreakpoint, validateLayout]);

  const saveWorkspaceToStorage = useCallback(() => {
    if (!userId) {
      console.warn('⚠️ No userId → workspace not saved');
      return false;
    }

    try {
      const currentLayout = getCurrentLayout();
      
      localStorage.setItem(
        `dashboard-workspace-${userId}`,
        JSON.stringify({ lg: currentLayout })
      );
      
      dispatch(saveAllTableStatesForUser(userId));
      
      // console.log('✅ Workspace saved (panels + table states)');
      return true;
    } catch (error) {
      console.error('❌ Error saving workspace:', error);
      return false;
    }
  }, [getCurrentLayout, userId, dispatch]);

  const loadWorkspace = useCallback(() => {
    if (!userId) {
      setLayouts(getDefaultLayout());
      console.log('⚠️ loadWorkspace skipped, no userId');
      return false;
    }

    try {
      const savedWorkspace = localStorage.getItem(`dashboard-workspace-${userId}`);

      if (savedWorkspace) {
        const parsed = JSON.parse(savedWorkspace);

        if (parsed && parsed.lg && Array.isArray(parsed.lg)) {
          setLayouts({ lg: parsed.lg });
          return true;
        } else {
          console.warn('⚠️ Saved workspace invalid format → fallback to default');
          setLayouts(getDefaultLayout());
          return false;
        }
      } else {
        console.log('ℹ️ No saved workspace → using default');
        setLayouts(getDefaultLayout());
        return false;
      }
    } catch (error) {
      console.error('❌ Error loading workspace:', error);
      setLayouts(getDefaultLayout());
      return false;
    }
  }, [userId]);

  const saveWorkspace = useCallback(() => {
    const success = saveWorkspaceToStorage();
    if (success) {
      alert('Workspace saved successfully!');
    } else {
      alert('Error saving workspace');
    }
  }, [saveWorkspaceToStorage]);

  const saveWorkspaceToFile = useCallback(() => {
    try {
      const currentLayout = getCurrentLayout();
      
      const workspaceData = {
        version: '2.0',
        timestamp: new Date().toISOString(),
        userId: userId,
        panelLayout: { lg: currentLayout },
        tableStates: tableStates,
      };
      
      const dataStr = JSON.stringify(workspaceData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `workspace-${userId}-${new Date().toISOString().split('T')[0]}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('💾 Workspace exported to file (with table states)');
      alert('Workspace exported successfully! (includes column widths, sorting, and search settings)');
    } catch (error) {
      console.error('Error exporting workspace:', error);
      alert('Error exporting workspace');
    }
  }, [getCurrentLayout, userId, tableStates]);

  const loadWorkspaceFromFile = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = function(e) {
      try {
        const parsed = JSON.parse(e.target.result);
        
        let layoutToLoad;
        let tableStatesToLoad;
        
        if (parsed.version === '2.0' && parsed.panelLayout && parsed.tableStates) {
          layoutToLoad = parsed.panelLayout.lg;
          tableStatesToLoad = parsed.tableStates;
          console.log('📂 Loading workspace v2.0 (with table states)');
        } else if (parsed.lg && Array.isArray(parsed.lg)) {
          layoutToLoad = parsed.lg;
          tableStatesToLoad = null;
          console.log('📂 Loading workspace v1.0 (panels only)');
        } else {
          alert('Invalid workspace file format');
          return;
        }
        
        if (layoutToLoad && Array.isArray(layoutToLoad)) {
          const validatedLayout = validateLayout(layoutToLoad);
          setLayouts({ lg: validatedLayout });
          
          localStorage.setItem(
            `dashboard-workspace-${userId}`, 
            JSON.stringify({ lg: validatedLayout })
          );
        }
        
        if (tableStatesToLoad) {
          dispatch(setAllTableStates({ 
            userId, 
            tableStates: tableStatesToLoad 
          }));
          
          localStorage.setItem(
            `tableStates_${userId}`, 
            JSON.stringify(tableStatesToLoad)
          );
          
          console.log('✅ Table states loaded:', Object.keys(tableStatesToLoad).length, 'tables');
        }
        
        alert(
          tableStatesToLoad 
            ? 'Workspace loaded! Table settings will apply when tables refresh.'
            : 'Workspace loaded! (panels only - no table settings in this file)'
        );
        
      } catch (error) {
        console.error('Error parsing workspace file:', error);
        alert('Invalid workspace file');
      }
    };
    reader.readAsText(file);
  }, [userId, validateLayout, dispatch]);

  const resetToDefault = useCallback(() => {
    if (window.confirm('Are you sure you want to reset to default workspace? This will reset panel positions AND table settings (column widths, sorting, search).')) {
      setLayouts(getDefaultLayout());
      localStorage.removeItem(`dashboard-workspace-${userId}`);
      
      localStorage.removeItem(`tableStates_${userId}`);
      dispatch(setAllTableStates({ userId, tableStates: {} }));
      
      alert('Workspace reset to default!');
      // setTimeout(() => window.location.reload(), 500);
    }
  }, [userId, dispatch]);

  useImperativeHandle(ref, () => ({
    saveWorkspace,
    resetToDefault,
    saveWorkspaceToFile,
    loadWorkspaceFromFile,
    saveWorkspaceToStorage
  }));

  useEffect(() => {
    setIsMounted(false);
    loadWorkspace();
    setIsMounted(true);
  }, [userId, loadWorkspace]);

  // useEffect(() => {
  //   if (userId) {
  //     console.log('🔄 Loading table states for user:', userId);
  //     dispatch(loadTableStatesForUser(userId));
  //   }
  // }, [userId, dispatch]);

  const onLayoutChange = useCallback((currentLayout, allLayouts) => {
    if (!isMounted) return;
    
    let safeLayouts = { ...allLayouts };
    let shouldForceLgBreakpoint = false;
    
    if (!safeLayouts.lg && safeLayouts.xxs) {
      console.warn('🚨 CORRUPTED LAYOUT DETECTED: lg missing, repairing...');
      shouldForceLgBreakpoint = true;
      
      if (layouts.lg) {
        safeLayouts.lg = layouts.lg;
      } else {
        safeLayouts.lg = safeLayouts.xxs.map(item => ({
          ...item,
          x: item.x * 6,
          w: Math.max(item.minW || 4, item.w * 6),
          minW: item.minW || 4,
          maxW: item.maxW || 12
        }));
      }
    }
    
    const breakpoints = ['lg', 'md', 'sm', 'xs', 'xxs'];
    breakpoints.forEach(bp => {
      if (!safeLayouts[bp] || !Array.isArray(safeLayouts[bp])) {
        safeLayouts[bp] = getDefaultLayout()[bp] || validateLayout(getDefaultLayout().lg);
      }
    });

    setLayouts(safeLayouts);
    
    if (shouldForceLgBreakpoint && currentBreakpoint !== 'lg') {
      setCurrentBreakpoint('lg');
    }
    
    saveWorkspaceToStorage();
  }, [isMounted, saveWorkspaceToStorage, layouts.lg, currentBreakpoint, validateLayout]);

  useEffect(() => {
    if (!layouts.lg && layouts.xxs) {
      console.error('🚨 LAYOUT CORRUPTION DETECTED - lg layout missing!');
      console.log('🔄 Triggering emergency layout recovery...');
      
      loadWorkspace();
      
      const recoveryTimer = setTimeout(() => {
        if (!layouts.lg) {
          setLayouts(getDefaultLayout());
        }
      }, 1000);
      
      return () => clearTimeout(recoveryTimer);
    }
  }, [layouts, loadWorkspace]);

  const onBreakpointChange = useCallback((newBreakpoint) => {
    setCurrentBreakpoint(newBreakpoint);
  }, []);

  const renderPanelContent = (panelId) => {
    const contentMap = {
      'reference-tabs': React.createElement(RefDataTabs),
      'buy-order-book': React.createElement(BuyOrderBookTable3),
      'sell-order-book': React.createElement(SellOrderBookTable3),
      'order-form': React.createElement(OrderForm),
      'users-tabs': React.createElement(UserDataTabs),
      'misc-tables': React.createElement(MiscTabs)
    };
    
    return contentMap[panelId] || React.createElement('div', null, 'Unknown panel: ' + panelId);
  };

  const renderPanels = () => {
    const currentLayout = getCurrentLayout();
    const noHeaderPanels = ['reference-tabs', 'users-tabs', 'misc-tables'];
    
    return currentLayout.map((item) => {
      const panelClass = 'dashboard-panel' + (item.static ? ' static-panel' : '');
      const title = item.i.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

      const panelContent = [
        !noHeaderPanels.includes(item.i) && React.createElement(
          'div',
          { 
            className: 'dashboard-panel-header',
            key: 'header'
          },
          React.createElement('h3', { className: 'panel-title' }, title)
        ),
        React.createElement(
          'div',
          { 
            className: 'dashboard-panel-content',
            key: 'content'
          },
          renderPanelContent(item.i)
        )
      ].filter(Boolean);

      if (noHeaderPanels.includes(item.i)) {
        panelContent.unshift(
          React.createElement(
            'div',
            {
              className: 'dashboard-drag-handle',
              key: 'drag-handle',
              style: {
                height: '12px',
                background: '#333',
                cursor: 'move',
                borderBottom: '1px solid #555'
              }
            }
          )
        );
      }

      return React.createElement(
        'div',
        { 
          key: item.i, 
          className: panelClass,
        },
        panelContent
      );
    });
  };

  const currentLayout = getCurrentLayout();

  if (!isMounted) {
    return React.createElement('div', { className: 'dashboard-container' }, 'Loading...');
  }

  const gridLayout = React.createElement(
    ResponsiveGridLayout,
    {
      className: 'dashboard-layout',
      layouts: { [currentBreakpoint]: currentLayout },
      breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
      cols: { lg: 36, md: 24, sm: 12, xs: 6, xxs: 4 },
      rowHeight: 41,
      onLayoutChange: onLayoutChange,
      onBreakpointChange: onBreakpointChange,
      isDraggable: true,
      isResizable: true,
      margin: [8, 8],
      containerPadding: [10, 10],
      autoSize: true,
      useCSSTransforms: true,
      compactType: null,
      draggableHandle: '.dashboard-panel-header, .dashboard-drag-handle',
      draggableCancel: '.dashboard-panel-content, .dashboard-panel-content *',
    },
    renderPanels()
  );

  return React.createElement(
    'div',
    { className: 'dashboard-container' },
    GlobalStyles,
    gridLayout
  );
});

export default Dashboard;